import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Drawer } from '../../componentes/sobreposicao';
import { get } from '../../nucleo/api';
import { data, dataHora, dinheiro, nomeCadastro, numero, textoLivre } from '../../nucleo/formato';
import { Linha, Secao } from './comum';
import { qtd, type RecebimentoDetalhe } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';
import { LinkParceiro } from '../../componentes/link-parceiro';

/** Ficha do recebimento (M10.3): a conferência de cada item, o lote, a entrada no Estoque e as contas a pagar geradas. */
export function DetalheRecebimento({ id, aoFechar }: { id: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  const q = useQuery({ queryKey: ['/compras/recebimentos', 'obter', id], queryFn: () => get<RecebimentoDetalhe>(`/compras/recebimentos/${id}`), enabled: !!id });
  const d = q.data;
  return (
    <Drawer aberto={!!id} aoFechar={aoFechar} largura="lg" titulo={d ? t('recebimento.fichaTitulo', { numero: d.numero }) : t('recebimento.tituloCurto')} rodape={<Botao onClick={aoFechar}>{tn('comum.fechar')}</Botao>}>
      {q.isPending && !!id && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {d && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Selo cor={d.divergente ? 'laranja' : 'verde'}>{d.divergente ? t('recebimento.comDivergencia') : t('recebimento.conferidoSelo')}</Selo>
          </div>
          <p className="numero mb-1 text-3xl font-semibold text-tinta-1">{anunciarOcultos(dinheiro(d.total_centavos, d.moeda))}</p>
          <p className="mb-4 text-sm text-tinta-3">{t('recebimento.valorRecebido')}</p>
          <dl className="divide-y divide-borda">
            <Linha rotulo={t('campos.pedido')}>
              <Link to={`/operacao/compras/pedidos?pedido=${d.pedido_id}`} className="text-acento-forte hover:underline" onClick={aoFechar}>
                {d.pedido_numero}
              </Link>
            </Linha>
            <Linha rotulo={t('campos.fornecedor')}>
              <LinkParceiro id={d.fornecedor_id} nome={d.fornecedor_nome} />
            </Linha>
            <Linha rotulo={t('campos.dataRecebimento')}>{data(d.data)}</Linha>
            <Linha rotulo={t('campos.deposito')}>{d.deposito_nome ?? t('pedido.depositoPadrao')}</Linha>
            <Linha rotulo={t('campos.registradoEm')}>{dataHora(d.created_at)}</Linha>
            {d.observacao && <Linha rotulo={t('campos.observacao')}>{textoLivre(d.observacao)}</Linha>}
          </dl>

          <Secao titulo={t('secoes.itensRecebidos')}>
            <ul className="divide-y divide-borda rounded-lg border border-borda">
              {d.itens_detalhe.map((i) => (
                <li key={i.id} className="flex min-w-0 flex-col gap-1 px-3 py-2 text-sm">
                  <span className="flex min-w-0 items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block truncate" title={i.produto_nome ?? undefined}>{nomeCadastro(i.produto_nome)}</span>
                      <TextoCortado className="numero block text-xs text-tinta-3">
                        {i.produto_codigo} · {qtd(i.quantidade, numero)} {i.unidade ?? ''} x {anunciarOcultos(dinheiro(i.preco_unitario_centavos, d.moeda))}
                      </TextoCortado>
                    </span>
                    <span className="numero shrink-0">{anunciarOcultos(dinheiro(i.total_centavos, d.moeda))}</span>
                  </span>
                  <span className="flex flex-wrap items-center gap-2 text-xs text-tinta-3">
                    <Selo cor={i.conferido ? 'verde' : 'laranja'}>{i.conferido ? t('recebimento.conferidoSelo') : t('recebimento.naoConferido')}</Selo>
                    {i.lote_codigo && (
                      <span className="numero">
                        {t('campos.lote')} {i.lote_codigo}
                        {i.lote_validade ? ` · ${t('campos.validadeLote')} ${data(i.lote_validade)}` : ''}
                      </span>
                    )}
                    {i.observacao && <span className="truncate" title={textoLivre(i.observacao)}>{textoLivre(i.observacao)}</span>}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-tinta-3">{t('recebimento.entradaNoEstoque')}</p>
          </Secao>

          <Secao titulo={t('secoes.contasAPagar')}>
            {d.documentos.length === 0 ? (
              <p className="text-sm text-tinta-3">{t('recebimento.semContas')}</p>
            ) : (
              <ul className="divide-y divide-borda rounded-lg border border-borda">
                {d.documentos.map((c, n) => (
                  <li key={c.id} className="flex min-w-0 items-center justify-between gap-3 px-3 py-2 text-sm">
                    <Link to={`/financeiro/contas/a-pagar?parceiro_id=${d.fornecedor_id}`} className="min-w-0 text-acento-forte hover:underline" onClick={aoFechar}>
                      <TextoCortado className="block">
                        {d.documentos.length > 1 ? t('recebimento.parcela', { n: n + 1, total: d.documentos.length }) : t('recebimento.contaAPagar')}
                      </TextoCortado>
                    </Link>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="numero text-xs text-tinta-3">{t('recebimento.venceEm', { data: data(c.vencimento) })}</span>
                      <span className="numero">{anunciarOcultos(dinheiro(c.valor_centavos, d.moeda))}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Secao>
        </>
      )}
    </Drawer>
  );
}
